import { Divider, Flex, Group, SegmentedControl, Select } from '@byecode/ui'
import { WechatProfitSharingPlatformEnum, WechatProfitSharingTypeEnum } from '@lighthouse/core'
import {
    canBeUpstreamNodeTypes,
    emptyBoolField,
    emptyNumberField,
    emptyTextField,
    type FlowNode,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    type NodeTypes,
    OtherPlatformNodeType,
    OUTPUT_SINGLE_DATA_NODES,
    useAtomData,
    type WeChatProfitSharingActionConfig
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { WechatPaymentAccountSelect } from '@/components/WechatPaymentAccountSelect'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import OutputVariable from '../../../OutputVariable'
import * as CM from '../../styles'
import { SelectMultipleRecord } from '../component/SelectMultipleRecord'
import { PROFIT_SHARING_PLATFORM_OPTIONS, PROFIT_SHARING_TYPE_OPTIONS } from './constants'

interface WeChatProfitSharingActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    value: WeChatProfitSharingActionConfig
    onChange: (value: WeChatProfitSharingActionConfig) => void
}

export const WeChatProfitSharingActionConfigure: React.FC<WeChatProfitSharingActionConfigureProps> = ({
    allParentNodes,
    actionTypeSwitcher,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const receiver = value.profitSharingReceiver
    const profitSharingReceiverNodeId = receiver?.nodeId
    const profitSharingType = receiver?.profitSharingType
    const singleRecordNodes = useMemo(() => {
        return allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType))
    }, [allParentNodes])

    const profitSharingReceiverNodeAndSingleRecordNodes = useMemo(() => {
        return allParentNodes.filter(node => canBeUpstreamNodeTypes.has(node.data.nodeType) || node.id === profitSharingReceiverNodeId)
    }, [allParentNodes, profitSharingReceiverNodeId])

    const textUserOption = useGetUserRecordOptions()

    const textUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: singleRecordNodes,
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, singleRecordNodes, workflowList]
    )

    const boolUserOption = useGetUserRecordOptions()

    const boolUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: singleRecordNodes,
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, singleRecordNodes, workflowList]
    )

    const numberUserOption = useGetUserRecordOptions()

    const profitSharingReceiverNumberUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: profitSharingReceiverNodeAndSingleRecordNodes,
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, profitSharingReceiverNodeAndSingleRecordNodes, workflowList]
    )

    const profitSharingReceiverTextUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: profitSharingReceiverNodeAndSingleRecordNodes,
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, profitSharingReceiverNodeAndSingleRecordNodes, workflowList]
    )

    const header = useMemo(() => {
        if (!actionTypeSwitcher) {
            return null
        }
        return (
            <>
                <Group label="动作配置">
                    <ListItem4ByecodeUi>
                        <CM.FormItemLabelWrapper>
                            <CM.FormItemLabel>类型</CM.FormItemLabel>
                        </CM.FormItemLabelWrapper>
                        <CM.FormItemContent>{actionTypeSwitcher}</CM.FormItemContent>
                    </ListItem4ByecodeUi>
                </Group>
                <Divider />
            </>
        )
    }, [actionTypeSwitcher])

    return (
        <CM.Container>
            {header}
            <WechatPaymentAccountSelect
                value={value?.paymentAccountId}
                onChange={paymentAccountId => onChange({ ...value, paymentAccountId })}
            />
            <Divider />
            <Group label="目标分账订单">
                <CM.FieldList>
                    <CM.FieldItem>
                        <PopoverQuestion
                            type="outTradeNo"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <CM.FieldTitle required>订单号</CM.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            value={value.outTradeNo}
                            onBlur={outTradeNo => onChange({ ...value, outTradeNo })}
                            autoHeight
                            userOption={textUserOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <PopoverQuestion
                            type="profitSharingOrderId"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <CM.FieldTitle required>商户分账单号</CM.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            value={value.profitSharingOrderId}
                            onBlur={profitSharingOrderId => onChange({ ...value, profitSharingOrderId })}
                            autoHeight
                            userOption={textUserOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <PopoverQuestion
                            type="isUnfreezeRemainingFund"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <CM.FieldTitle required>是否解冻剩余未分资金</CM.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('BOOL')}
                            disabled={disabledWithVersion}
                            field={emptyBoolField}
                            style={{
                                width: '100%',
                                fontSize: 14
                            }}
                            value={value.isUnfreezeRemainingFund}
                            onBlur={isUnfreezeRemainingFund => onChange({ ...value, isUnfreezeRemainingFund })}
                            autoHeight
                            userOption={boolUserOption}
                            upstreamOptions={boolUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                </CM.FieldList>
            </Group>
            <Divider />
            <Group label="分账接收方列表">
                <Flex direction="column" gap={8}>
                    <SelectMultipleRecord
                        description="从上游节点输出的记录，以此为依据创建分账接收方列表。"
                        value={receiver?.nodeId}
                        onChange={nodeId =>
                            onChange({
                                ...value,
                                profitSharingReceiver: {
                                    ...(receiver || ({} as WeChatProfitSharingActionConfig['profitSharingReceiver'])),
                                    nodeId
                                }
                            })
                        }
                        allParentNodes={allParentNodes}
                    />
                    <CM.FieldList>
                        <CM.FieldItem>
                            <Flex gap={16}>
                                <CM.FieldTitle required>分账接收方类型</CM.FieldTitle>
                            </Flex>
                            <Select
                                style={{ width: '100%' }}
                                options={PROFIT_SHARING_TYPE_OPTIONS}
                                value={receiver?.profitSharingType}
                                onChange={profitSharingType =>
                                    onChange({
                                        ...value,
                                        profitSharingReceiver: {
                                            ...(receiver || ({} as WeChatProfitSharingActionConfig['profitSharingReceiver'])),
                                            profitSharingType: profitSharingType as WechatProfitSharingTypeEnum
                                        }
                                    })
                                }
                            />
                        </CM.FieldItem>
                        <CM.FieldItem>
                            <PopoverQuestion
                                type="profitSharingAccount"
                                renderTarget={(node, ref) => (
                                    <Flex gap={16} ref={ref} alignItems="center">
                                        <CM.FieldTitle required>分账接收方账号</CM.FieldTitle>
                                        {node}
                                    </Flex>
                                )}
                            />
                            {profitSharingType === 'PERSONAL_OPENID' && (
                                <SegmentedControl
                                    fullWidth
                                    data={PROFIT_SHARING_PLATFORM_OPTIONS}
                                    value={receiver?.profitSharingPlatform}
                                    onChange={profitSharingPlatform =>
                                        onChange({
                                            ...value,
                                            profitSharingReceiver: {
                                                ...(receiver || ({} as WeChatProfitSharingActionConfig['profitSharingReceiver'])),
                                                profitSharingPlatform: profitSharingPlatform as WechatProfitSharingPlatformEnum
                                            }
                                        })
                                    }
                                />
                            )}
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                disabled={disabledWithVersion}
                                value={receiver?.profitSharingAccount}
                                onBlur={profitSharingAccount =>
                                    onChange({
                                        ...value,
                                        profitSharingReceiver: {
                                            ...(receiver || ({} as WeChatProfitSharingActionConfig['profitSharingReceiver'])),
                                            profitSharingAccount
                                        }
                                    })
                                }
                                autoHeight
                                userOption={textUserOption}
                                upstreamOptions={profitSharingReceiverTextUpstreamOptions}
                                dataSourceList={dataSourceList}
                            />
                        </CM.FieldItem>
                        <CM.FieldItem>
                            <PopoverQuestion
                                type="profitSharingAmount"
                                renderTarget={(node, ref) => (
                                    <Flex gap={16} ref={ref} alignItems="center">
                                        <CM.FieldTitle required>分账金额</CM.FieldTitle>
                                        {node}
                                    </Flex>
                                )}
                            />
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('NUMBER')}
                                disabled={disabledWithVersion}
                                field={emptyNumberField}
                                style={{
                                    width: '100%',
                                    fontSize: 14
                                }}
                                value={receiver?.profitSharingAmount}
                                onBlur={profitSharingAmount =>
                                    onChange({
                                        ...value,
                                        profitSharingReceiver: {
                                            ...(receiver || ({} as WeChatProfitSharingActionConfig['profitSharingReceiver'])),
                                            profitSharingAmount
                                        }
                                    })
                                }
                                autoHeight
                                userOption={numberUserOption}
                                upstreamOptions={profitSharingReceiverNumberUpstreamOptions}
                                dataSourceList={dataSourceList}
                            />
                        </CM.FieldItem>
                        <CM.FieldItem>
                            <PopoverQuestion
                                type="profitSharingDescription"
                                renderTarget={(node, ref) => (
                                    <Flex gap={16} ref={ref} alignItems="center">
                                        <CM.FieldTitle required>分账描述</CM.FieldTitle>
                                        {node}
                                    </Flex>
                                )}
                            />
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                disabled={disabledWithVersion}
                                field={emptyTextField}
                                style={{
                                    width: '100%',
                                    fontSize: 14
                                }}
                                value={receiver?.profitSharingDescription}
                                onBlur={profitSharingDescription =>
                                    onChange({
                                        ...value,
                                        profitSharingReceiver: {
                                            ...(receiver || ({} as WeChatProfitSharingActionConfig['profitSharingReceiver'])),
                                            profitSharingDescription
                                        }
                                    })
                                }
                                autoHeight
                                userOption={textUserOption}
                                upstreamOptions={profitSharingReceiverTextUpstreamOptions}
                                dataSourceList={dataSourceList}
                            />
                        </CM.FieldItem>
                    </CM.FieldList>
                </Flex>
            </Group>
            <OutputVariable nodeType={OtherPlatformNodeType.WECHAT_PROFIT_SHARING} />
        </CM.Container>
    )
}
